/**
 * TagsListPage — F031
 *
 * Kartenraster für Tag-Verwaltung (Prototyp-Parität, v2-final/tags.html):
 * pro Tag ein Card mit Farb-Pill, Hotspot-/Personen-Chipline, "Auf Hotspots
 * anwenden"-Aktion und optionalem Dashboard-Scope-Link.
 *
 * Farbe: Backend kennt kein Tag.color-Feld (siehe api/v2 TagsController) —
 * daher rein clientseitig deterministisch aus der Tag-ID abgeleitet, nicht
 * persistiert. Kein Farbwähler im Create-Modal, um keine Auswahl vorzutäuschen,
 * die verworfen würde.
 */
import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Plus, X as XIcon, Pencil } from 'lucide-react'
import { ModalSheet } from '../../components/ui/ModalSheet'
import { useTags, useCreateTag, useUpdateTag, useDeleteTag, useAddHotspotsToTag, useRemoveHotspotsFromTag, type Tag } from './useTags'
import { useHotspots } from '../hotspots/useHotspots'
import { hotspotDisplayName } from '../../lib/hotspotDisplayName'
import './tags.css'

// Prototyp-Palette (tags.html Zeile 57): primary/accent/success/warning/danger.
const COLOR_TOKENS = ['var(--primary)', 'var(--accent)', 'var(--success)', 'var(--warning)', 'var(--danger)']
function colorForTag(id: number): string {
  return COLOR_TOKENS[Math.abs(id) % COLOR_TOKENS.length]
}

// --- Loading Skeleton ---

function LoadingSkeleton() {
  const { t } = useTranslation('tags')
  return (
    <div className="tag-grid" aria-busy="true" aria-label={t('loading.tags')}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="tag-card" aria-hidden="true">
          <div className="skeleton-line" style={{ width: 100, height: 20 }} />
          <div className="skeleton-line" style={{ width: 140, height: 12 }} />
        </div>
      ))}
    </div>
  )
}

// --- Error State ---

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('tags')
  return (
    <div className="tags-state" role="alert">
      <p>{t('error.loadTags')}</p>
      <button className="btn btn-primary" onClick={onRetry}>
        {t('common:actions.retry')}
      </button>
    </div>
  )
}

// --- Empty State ---

function EmptyState() {
  const { t } = useTranslation('tags')
  return (
    <div className="tags-state">
      <p>{t('empty.noTags')}</p>
      <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
        {t('empty.noTagsHint')}
      </p>
    </div>
  )
}

// --- Tag Card ---

type TagCardProps = {
  tag: Tag
  onEdit: (tag: Tag) => void
  onDelete: (tag: Tag) => void
  onApply: (tag: Tag) => void
}

function TagCard({ tag, onEdit, onDelete, onApply }: TagCardProps) {
  const { t } = useTranslation('tags')
  const color = colorForTag(tag.id)
  // BUGFIX #48: Namen kommen aus GET /api/v2/tags selbst (tag.hotspots /
  // tag.contacts). Vorher wurden sie aus der Hotspot-/Kontaktliste
  // rekonstruiert — die deckt nur owned+active ab und war auf 100 Eintraege
  // gekappt, weshalb die Karten rohe IDs ("#177615") zeigten.
  const hotspots = tag.hotspots ?? (tag.hotspot_ids ?? []).map((id) => ({ id, name: '', address: '' }))
  const contacts = tag.contacts ?? (tag.contact_ids ?? []).map((id) => ({ id, name: '', email: '' }))

  return (
    <div className="tag-card" data-testid={`tag-row-${tag.id}`}>
      <div className="tag-card__head">
        <span className="pill" style={{ borderColor: color, color }}>
          <span className="tag-color-swatch" />
          {tag.name}
        </span>
        <div className="tag-card__actions">
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            title={t('list.applyBtn')}
            onClick={() => onApply(tag)}
            data-testid={`tag-apply-${tag.id}`}
          >
            <Plus size={12} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            title={t('common:actions.edit')}
            onClick={() => onEdit(tag)}
            data-testid={`tag-edit-${tag.id}`}
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-icon"
            title={t('common:actions.delete')}
            onClick={() => onDelete(tag)}
            data-testid={`tag-delete-${tag.id}`}
          >
            <XIcon size={12} />
          </button>
        </div>
      </div>

      <div className="tag-card__meta">
        <span>{t('list.hotspotsCount', { count: tag.hotspots_count })}</span>
        {' · '}
        <span>{t('list.contactsCount', { count: tag.contacts_count })}</span>
      </div>

      {(hotspots.length > 0 || contacts.length > 0) && (
        <div className="chipline">
          {hotspots.map((h) => (
            <Link key={`h${h.id}`} to="/hotspots/$id" params={{ id: String(h.id) }} className="tag-chip">
              {hotspotDisplayName(h.name, h.address, t('empty.unknownHotspot'))}
            </Link>
          ))}
          {contacts.map((c) => (
            <Link key={`c${c.id}`} to="/contacts/$id" params={{ id: String(c.id) }} className="tag-chip tag-chip--person">
              {c.name || `#${c.id}`}
            </Link>
          ))}
        </div>
      )}

      {tag.dashboard ? (
        <Link
          to="/dashboard"
          search={{ tag: tag.id }}
          className="tag-card__dashboard-link"
          data-testid={`tag-dashboard-${tag.id}`}
        >
          {t('list.viewDashboard', { name: tag.name })}
        </Link>
      ) : (
        <Link
          to="/tags/$id"
          params={{ id: String(tag.id) }}
          className="tag-card__dashboard-disabled"
          title={t('list.dashboardDisabledHint')}
          data-testid={`tag-dashboard-disabled-${tag.id}`}
        >
          {t('list.dashboardDisabled')}
        </Link>
      )}
    </div>
  )
}

// --- Create/Edit Modal ---

type TagModalProps = {
  tag?: Tag | null
  onClose: () => void
  onSave: (data: { name: string; dashboard?: boolean }) => void
  isPending: boolean
}

function TagModal({ tag, onClose, onSave, isPending }: TagModalProps) {
  const { t } = useTranslation('tags')
  const [name, setName] = useState(tag?.name ?? '')
  const [dashboard, setDashboard] = useState(tag?.dashboard ?? true)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    onSave({ name: name.trim(), dashboard })
  }

  return (
    <ModalSheet
      open
      onClose={onClose}
      title={tag ? t('modal.editTitle') : t('modal.createTitle')}
      data-testid="tag-form-modal"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </button>
          <button type="submit" form="tag-form" className="btn btn-primary" disabled={isPending || !name.trim()}>
            {isPending ? t('modal.savingLabel') : t('common:actions.save')}
          </button>
        </>
      }
    >
      <form id="tag-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <label className="label" htmlFor="tag-name">{t('modal.fieldName')}</label>
          <input
            id="tag-name"
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('modal.namePlaceholder')}
            required
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            id="tag-dashboard"
            type="checkbox"
            checked={dashboard}
            onChange={(e) => setDashboard(e.target.checked)}
          />
          <label htmlFor="tag-dashboard" style={{ fontSize: 13, cursor: 'pointer' }}>
            {t('modal.showOnDashboard')}
          </label>
        </div>
      </form>
    </ModalSheet>
  )
}

// --- Delete Confirm Modal ---

type DeleteModalProps = {
  tag: Tag
  onClose: () => void
  onConfirm: () => void
  isPending: boolean
}

function DeleteModal({ tag, onClose, onConfirm, isPending }: DeleteModalProps) {
  const { t } = useTranslation('tags')
  return (
    <ModalSheet
      open
      onClose={onClose}
      title={t('modal.deleteTitle')}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="confirm-delete-tag"
          >
            {isPending ? t('modal.deletingLabel') : t('common:actions.delete')}
          </button>
        </>
      }
    >
      <p>{t('modal.deleteConfirm', { name: tag.name })}</p>
      <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>
        {t('modal.deleteWarning')}
      </p>
    </ModalSheet>
  )
}

// --- Apply-to-hotspots Modal (F1 Prototyp-Aktion, nutzt bestehende add/remove_hotspots) ---

type ApplyModalProps = {
  tag: Tag
  onClose: () => void
}

function ApplyModal({ tag, onClose }: ApplyModalProps) {
  const { t } = useTranslation('tags')
  const { data: hotspots, isLoading } = useHotspots({ per_page: 100 })
  const addHotspots = useAddHotspotsToTag()
  const removeHotspots = useRemoveHotspotsFromTag()
  const [selected, setSelected] = useState<number[]>(tag.hotspot_ids ?? [])
  const isPending = addHotspots.isPending || removeHotspots.isPending

  function toggle(id: number) {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  async function handleApply() {
    const before = tag.hotspot_ids ?? []
    const toAdd = selected.filter((id) => !before.includes(id))
    const toRemove = before.filter((id) => !selected.includes(id))
    if (toAdd.length > 0) await addHotspots.mutateAsync({ tagId: tag.id, hotspotIds: toAdd })
    if (toRemove.length > 0) await removeHotspots.mutateAsync({ tagId: tag.id, hotspotIds: toRemove })
    onClose()
  }

  return (
    <ModalSheet
      open
      onClose={onClose}
      title={t('modal.applyTitle', { name: tag.name })}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleApply} disabled={isPending}>
            {isPending ? t('modal.savingLabel') : t('modal.applyBtn')}
          </button>
        </>
      }
    >
      {isLoading && <p className="muted">{t('loading.inline')}</p>}
      {!isLoading && (!hotspots || hotspots.length === 0) && <p className="muted">{t('modal.noHotspots')}</p>}
      {!isLoading && hotspots && hotspots.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 360, overflowY: 'auto' }}>
          {hotspots.map((h) => (
            <label key={h.id} className="row" style={{ gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13 }}>
              <input
                type="checkbox"
                checked={selected.includes(Number(h.id))}
                onChange={() => toggle(Number(h.id))}
                style={{ accentColor: 'var(--primary)' }}
              />
              {hotspotDisplayName(h.name, h.address, t('empty.unknownHotspot'))}
            </label>
          ))}
        </div>
      )}
    </ModalSheet>
  )
}

// --- Main Page ---

export function TagsListPage() {
  const { t } = useTranslation('tags')
  const { data: tags, isLoading, error, refetch } = useTags()
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()
  const deleteTag = useDeleteTag()

  const [showModal, setShowModal] = useState(false)
  const [editingTag, setEditingTag] = useState<Tag | null>(null)
  const [deletingTag, setDeletingTag] = useState<Tag | null>(null)
  const [applyingTag, setApplyingTag] = useState<Tag | null>(null)

  function handleCreate(data: { name: string; dashboard?: boolean }) {
    createTag.mutate(data, {
      onSuccess: () => setShowModal(false),
    })
  }

  function handleUpdate(data: { name: string; dashboard?: boolean }) {
    if (!editingTag) return
    updateTag.mutate({ id: editingTag.id, ...data }, {
      onSuccess: () => {
        setEditingTag(null)
      },
    })
  }

  function handleDelete() {
    if (!deletingTag) return
    deleteTag.mutate({ id: deletingTag.id }, {
      onSuccess: () => setDeletingTag(null),
    })
  }

  return (
    <div className="tags-page">
      <div className="tags-page__header">
        <div>
          <h1>{t('page.title')}</h1>
          <p className="muted" style={{ marginTop: 4 }}>
            {t('page.subtitle')}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
          data-testid="create-tag-btn"
        >
          {t('list.createBtn')}
        </button>
      </div>

      {isLoading && <LoadingSkeleton />}

      {error && <ErrorState onRetry={refetch} />}

      {!isLoading && !error && tags && tags.length === 0 && <EmptyState />}

      {!isLoading && !error && tags && tags.length > 0 && (
        <div className="tag-grid">
          {tags.map((tag) => (
            <TagCard
              key={tag.id}
              tag={tag}
              onEdit={(t) => setEditingTag(t)}
              onDelete={(t) => setDeletingTag(t)}
              onApply={(t) => setApplyingTag(t)}
            />
          ))}
        </div>
      )}

      {showModal && (
        <TagModal
          onClose={() => setShowModal(false)}
          onSave={handleCreate}
          isPending={createTag.isPending}
        />
      )}

      {editingTag && (
        <TagModal
          tag={editingTag}
          onClose={() => setEditingTag(null)}
          onSave={handleUpdate}
          isPending={updateTag.isPending}
        />
      )}

      {deletingTag && (
        <DeleteModal
          tag={deletingTag}
          onClose={() => setDeletingTag(null)}
          onConfirm={handleDelete}
          isPending={deleteTag.isPending}
        />
      )}

      {applyingTag && <ApplyModal tag={applyingTag} onClose={() => setApplyingTag(null)} />}
    </div>
  )
}
